import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Section, SectionItem, Newsletter } from '@/components';
import {
  getPostBySlug,
  getRelatedPosts,
  getAdjacentPosts,
  formatDate,
  type BlogCategory,
  type BlogPost,
  type TableOfContentsItem,
} from '@/data/blog';

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = slug ? getPostBySlug(slug) : undefined;
  const relatedPosts = slug ? getRelatedPosts(slug, 3) : [];
  const { prev, next } = slug ? getAdjacentPosts(slug) : { prev: null, next: null };

  // Scroll to top when post changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Redirect to blog listing if post not found
  useEffect(() => {
    if (slug && !post) {
      navigate('/blog', { replace: true });
    }
  }, [slug, post, navigate]);

  if (!post) {
    return null;
  }

  return (
    <>
      <title>{post.title} | Omniforge Blog</title>
      <meta name="description" content={post.excerpt} />

      {/* Hero Section */}
      <section className="bg-ink pt-32 pb-16">
        <div className="container-marketing">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl mx-auto"
          >
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
              <Link to="/blog" className="hover:text-paper transition-colors">
                Blog
              </Link>
              <ChevronRightIcon className="w-4 h-4" />
              <span className="text-paper">{post.title}</span>
            </nav>

            {/* Category Badge */}
            <CategoryBadge category={post.category} />

            {/* Title */}
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-paper mt-4 mb-6">
              {post.title}
            </h1>

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-8">
              <div className="flex items-center gap-3">
                <img
                  src={post.author.imageUrl}
                  alt={post.author.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <div className="text-paper font-medium">{post.author.name}</div>
                  <div className="text-sm">{post.author.role}</div>
                </div>
              </div>
              <span className="hidden sm:inline">|</span>
              <span>{formatDate(post.publishedAt)}</span>
              <span>|</span>
              <span>{post.readTime} min read</span>
            </div>

            {/* Social Share */}
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">Share:</span>
              <SocialShareButtons title={post.title} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-paper -mt-8 relative z-10">
        <div className="container-marketing">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl mx-auto"
          >
            <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-elevated">
              <img
                src={post.imageUrl}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <Section background="paper">
        <div className="grid lg:grid-cols-4 gap-12 max-w-6xl mx-auto">
          {/* Table of Contents Sidebar (Desktop) */}
          {post.tableOfContents && post.tableOfContents.length > 0 && (
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-24">
                <TableOfContents items={post.tableOfContents} />
              </div>
            </aside>
          )}

          {/* Article Content */}
          <article
            className={`${
              post.tableOfContents && post.tableOfContents.length > 0
                ? 'lg:col-span-3'
                : 'lg:col-span-4 max-w-3xl mx-auto'
            }`}
          >
            <SectionItem>
              {post.content ? (
                <ArticleContent content={post.content} />
              ) : (
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted text-lg">{post.excerpt}</p>
                  <p className="text-muted">
                    Full article content coming soon. Check back later for the complete article.
                  </p>
                </div>
              )}

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-cream rounded-full text-sm text-muted"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Social Share (Bottom) */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-4">
                  <span className="text-muted text-sm">Share this article:</span>
                  <SocialShareButtons title={post.title} variant="dark" />
                </div>
              </div>
            </SectionItem>
          </article>
        </div>
      </Section>

      {/* Author Bio */}
      {post.author.bio && (
        <Section background="cream">
          <SectionItem>
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-soft">
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <img
                    src={post.author.imageUrl}
                    alt={post.author.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-1">
                      About {post.author.name}
                    </h3>
                    <p className="text-accent text-sm mb-3">{post.author.role}</p>
                    <p className="text-muted">{post.author.bio}</p>
                  </div>
                </div>
              </div>
            </div>
          </SectionItem>
        </Section>
      )}

      {/* Previous/Next Navigation */}
      {(prev || next) && (
        <Section background="paper">
          <div className="max-w-4xl mx-auto">
            <div className="grid sm:grid-cols-2 gap-6">
              {prev ? (
                <Link
                  to={`/blog/${prev.slug}`}
                  className="group p-6 bg-cream rounded-2xl hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2 text-sm text-muted mb-2">
                    <ChevronLeftIcon className="w-4 h-4" />
                    <span>Previous Article</span>
                  </div>
                  <h4 className="font-heading font-semibold group-hover:text-accent transition-colors line-clamp-2">
                    {prev.title}
                  </h4>
                </Link>
              ) : (
                <div />
              )}
              {next && (
                <Link
                  to={`/blog/${next.slug}`}
                  className="group p-6 bg-cream rounded-2xl hover:bg-gray-100 transition-colors text-right"
                >
                  <div className="flex items-center justify-end gap-2 text-sm text-muted mb-2">
                    <span>Next Article</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </div>
                  <h4 className="font-heading font-semibold group-hover:text-accent transition-colors line-clamp-2">
                    {next.title}
                  </h4>
                </Link>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <Section background="cream">
          <SectionItem>
            <div className="text-center mb-12">
              <h2 className="font-display text-h2 mb-4">Related Articles</h2>
              <p className="text-muted text-lg">Continue reading with these related posts</p>
            </div>
          </SectionItem>

          <div className="grid md:grid-cols-3 gap-6">
            {relatedPosts.map((relatedPost) => (
              <SectionItem key={relatedPost.id}>
                <RelatedPostCard post={relatedPost} />
              </SectionItem>
            ))}
          </div>
        </Section>
      )}

      {/* Newsletter CTA */}
      <Section background="paper">
        <div className="max-w-2xl mx-auto">
          <Newsletter />
        </div>
      </Section>
    </>
  );
}

// ============================================
// ARTICLE CONTENT RENDERER
// ============================================

function ArticleContent({ content }: { content: string }) {
  // Simple markdown-like rendering for the content
  // In a real app, you'd use a proper markdown parser like react-markdown
  const processContent = (text: string) => {
    const lines = text.trim().split('\n');
    const elements: React.ReactElement[] = [];
    let currentList: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let key = 0;

    const flushList = () => {
      if (currentList.length > 0 && listType) {
        const ListTag = listType;
        elements.push(
          <ListTag key={key++} className={listType === 'ol' ? 'list-decimal' : 'list-disc'}>
            {currentList.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: processInlineMarkdown(item) }} />
            ))}
          </ListTag>
        );
        currentList = [];
        listType = null;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] ?? '';
      const trimmed = line.trim();

      if (trimmed === '') {
        flushList();
        continue;
      }

      // Headers
      if (trimmed.startsWith('### ')) {
        flushList();
        const id = trimmed.slice(4).toLowerCase().replace(/[^a-z0-9]+/g, '-');
        elements.push(
          <h3 key={key++} id={id} className="scroll-mt-24">
            {trimmed.slice(4)}
          </h3>
        );
        continue;
      }

      if (trimmed.startsWith('## ')) {
        flushList();
        const id = trimmed.slice(3).toLowerCase().replace(/[^a-z0-9]+/g, '-');
        elements.push(
          <h2 key={key++} id={id} className="scroll-mt-24">
            {trimmed.slice(3)}
          </h2>
        );
        continue;
      }

      // Unordered list items
      if (trimmed.startsWith('- ')) {
        if (listType === 'ol') flushList();
        listType = 'ul';
        currentList.push(trimmed.slice(2));
        continue;
      }

      // Ordered list items
      const orderedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
      if (orderedMatch && orderedMatch[2]) {
        if (listType === 'ul') flushList();
        listType = 'ol';
        currentList.push(orderedMatch[2]);
        continue;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p key={key++} dangerouslySetInnerHTML={{ __html: processInlineMarkdown(trimmed) }} />
      );
    }

    flushList();
    return elements;
  };

  const processInlineMarkdown = (text: string) => {
    // Bold
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Links (basic)
    text = text.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-accent hover:underline">$1</a>');
    return text;
  };

  return (
    <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:font-semibold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-muted prose-p:leading-relaxed prose-li:text-muted prose-ul:my-4 prose-ol:my-4 prose-strong:text-ink">
      {processContent(content)}
    </div>
  );
}

// ============================================
// TABLE OF CONTENTS
// ============================================

function TableOfContents({ items }: { items: TableOfContentsItem[] }) {
  const handleClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-cream rounded-2xl p-6">
      <h4 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted mb-4">
        Table of Contents
      </h4>
      <nav>
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={handleClick(item.id)}
                className={`block text-sm hover:text-accent transition-colors ${
                  item.level === 3 ? 'pl-4 text-muted' : 'font-medium'
                }`}
              >
                {item.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

// ============================================
// RELATED POST CARD
// ============================================

function RelatedPostCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={post.imageUrl}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <CategoryBadge category={post.category} />
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-xs text-muted mb-2">
          <span>{formatDate(post.publishedAt)}</span>
          <span>|</span>
          <span>{post.readTime} min read</span>
        </div>
        <h3 className="font-heading font-semibold text-lg group-hover:text-accent transition-colors line-clamp-2">
          {post.title}
        </h3>
      </div>
    </Link>
  );
}

// ============================================
// CATEGORY BADGE
// ============================================

function CategoryBadge({ category }: { category: BlogCategory }) {
  const colors: Record<BlogCategory, string> = {
    growth: 'bg-green-100 text-green-800',
    operations: 'bg-blue-100 text-blue-800',
    technology: 'bg-purple-100 text-purple-800',
    'industry-news': 'bg-orange-100 text-orange-800',
  };

  const labels: Record<BlogCategory, string> = {
    growth: 'Growth',
    operations: 'Operations',
    technology: 'Technology',
    'industry-news': 'Industry News',
  };

  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${colors[category]}`}>
      {labels[category]}
    </span>
  );
}

// ============================================
// SOCIAL SHARE BUTTONS
// ============================================

interface SocialShareButtonsProps {
  title: string;
  variant?: 'light' | 'dark';
}

function SocialShareButtons({ title, variant = 'light' }: SocialShareButtonsProps) {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const buttonClass =
    variant === 'light'
      ? 'p-2 rounded-full bg-white/10 hover:bg-white/20 text-paper transition-colors'
      : 'p-2 rounded-full bg-cream hover:bg-gray-200 text-ink transition-colors';

  return (
    <div className="flex items-center gap-2">
      <a
        href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClass}
        aria-label="Share on Twitter"
      >
        <TwitterIcon className="w-5 h-5" />
      </a>
      <a
        href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClass}
        aria-label="Share on LinkedIn"
      >
        <LinkedInIcon className="w-5 h-5" />
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClass}
        aria-label="Share on Facebook"
      >
        <FacebookIcon className="w-5 h-5" />
      </a>
    </div>
  );
}

// ============================================
// ICONS
// ============================================

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}
